/**
 * Media utilities for handling relative and absolute URLs
 */

/**
 * Check if a path is a relative media path (starts with /op-uploads, /uploads, or /media)
 */
export function isRelativeMediaPath(path: string): boolean {
  return /^\/(op-uploads|uploads|media)\//.test(path);
}

/**
 * Check if a URL is a valid media URL (relative path, http/https, or data URL)
 */
export function isValidMediaUrl(url: string): boolean {
  if (!url) return false;
  return isRelativeMediaPath(url) || /^https?:\/\//.test(url) || /^data:/.test(url);
}

/**
 * Resolve a media URL to an absolute URL if it's relative
 * Returns undefined if input is empty or invalid
 */
export function resolveMediaUrl(input: string | undefined | null): string | undefined {
  if (!input) return undefined;
  
  // Already absolute URLs (http/https/data)
  if (/^(https?:\/\/|data:)/.test(input)) {
    return input;
  }
  
  // Relative paths - convert to absolute
  if (isRelativeMediaPath(input)) {
    // Use current origin as base URL
    return `${window.location.origin}${input}`;
  }
  
  // Handle paths starting with / (make them absolute)
  if (input.startsWith('/')) {
    return `${window.location.origin}${input}`;
  }
  
  // Return as-is for other cases (like Supabase URLs)
  return input;
}

/**
 * Get file type from URL or file extension
 */
export function getFileType(url: string): 'image' | 'video' | 'unknown' {
  const ext = url.split('.').pop()?.toLowerCase() || '';
  const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'bmp'];
  const videoExts = ['mp4', 'webm', 'ogg', 'avi', 'mov'];
  
  if (imageExts.includes(ext)) return 'image';
  if (videoExts.includes(ext)) return 'video';
  return 'unknown';
}

/**
 * Minimal shape for any entity that may provide a banner/background image.
 */
export interface BannerCapableEntity {
  banner_image_url?: string | null;
  featured_image_url?: string | null;
  logo_url?: string | null;
  icon_url?: string | null;
  gallery_urls?: string[] | null;
}

/**
 * Resolve best banner image for entities (sectors, companies, projects, partners, services).
 * Fallback chain:
 * 1) banner_image_url
 * 2) featured_image_url
 * 3) first gallery image (if any)
 * 4) logo_url
 * 5) icon_url
 * 6) provided fallback (if any) or site default background
 * Returns absolute URL when possible.
 */
export function resolveEntityBanner(
  entity: BannerCapableEntity | undefined | null,
  options?: { fallback?: string; preferGallery?: boolean; allowLogoFallback?: boolean }
): string | undefined {
  const preferGallery = options?.preferGallery ?? true;
  const allowLogoFallback = options?.allowLogoFallback ?? false;

  const candidates: Array<string | undefined | null> = [];
  if (entity) {
    candidates.push(entity.banner_image_url);
    candidates.push(entity.featured_image_url);
    if (preferGallery && Array.isArray(entity.gallery_urls) && entity.gallery_urls.length > 0) {
      candidates.push(entity.gallery_urls[0]);
    }
    if (allowLogoFallback) {
      candidates.push(entity.logo_url);
      candidates.push(entity.icon_url);
    }
  }
  // site-level default banner under public/
  const siteDefault = '/op-uploads/business-bg-1.png';
  candidates.push(options?.fallback || siteDefault, '/placeholder.svg');

  for (const src of candidates) {
    if (src && isValidMediaUrl(src)) {
      const resolved = resolveMediaUrl(src);
      if (resolved) return resolved;
    }
  }
  return undefined;
}